import { EWorldMapSetType } from '__generated__/globalTypes';
import { CtxMapV2 } from 'components/mapV2/contexts/CtxMapV2';
import { MapV2CategoryButton } from 'components/mapV2/elements/MapV2CategoryButton';
import { useCategoryTranslations } from 'components/mapV2/hooks/useCategoryTranslations';
import { ScrollHorizontal } from 'elements/functional/ScrollHorizontal';
import { useAnalytics } from 'libs/utils/site/analytics';
import { difference, union } from 'lodash';
import { useContext, useEffect, useState } from 'react';
import { CtxModal } from 'session/ctxModal';
import { CtxView } from 'session/ctxView';
import { Localization } from 'static/localization/localization';
import styled from 'styled-components/macro';

interface Props {
    className?: string;
}

export const MapV2Categories = (props: Props): JSX.Element => {
    const ctxView = useContext(CtxView);
    const ctxMapV2 = useContext(CtxMapV2);
    const ctxModal = useContext(CtxModal);
    const analytics = useAnalytics();
    const categories = ctxView?.map.worldMapSets.filter(
        (e) => e.type === EWorldMapSetType.CATEGORY
    );
    const categoryIds = categories.map((e) => e.worldMapSetId).concat('highlights');
    const [categoryId, setCategoryId] = useState(null);
    const categoryTranslations = useCategoryTranslations();

    useEffect(() => {
        if (categoryId) {
            const newVisibleMapSetIds = union(difference(ctxView.visibleMapSetIds, categoryIds), [
                categoryId
            ]);
            ctxView.setVisibleMapSetIds(newVisibleMapSetIds);
        } else {
            const newVisibleMapSetIds = union(ctxView.visibleMapSetIds, categoryIds);
            ctxView.setVisibleMapSetIds(newVisibleMapSetIds);
        }
        ctxModal.setDetailListModal(null);
    }, [categoryId]);

    const includeHighlightsCategory = ctxView.map.showHighlightedCategory;

    if (!categories.length && !includeHighlightsCategory) {
        return null;
    }

    const content = (
        <>
            <S_Category
                key={'all'}
                label={Localization.MAP_V2_CATEGORY_SHOW_ALL}
                imgSrc={
                    'https://dvtqxks0zalev.cloudfront.net/53819dd3-5d52-45db-95d9-b35e11166524.png'
                }
                isActive={categoryId === null}
                onClick={() => {
                    setCategoryId(null);
                    analytics.track('event_ui.filter.clicked', {
                        title: 'all'
                    });
                }}
            />
            {includeHighlightsCategory && (
                <S_Category
                    key={'highlights'}
                    label={Localization.MAP_V2_CATEGORY_HIGHLIGHTS}
                    imgSrc={
                        'https://dvtqxks0zalev.cloudfront.net/8480b398-03a0-4372-b46e-bab3a8cd6946.png'
                    }
                    isActive={categoryId === 'highlights'}
                    onClick={() => {
                        setCategoryId('highlights');
                        analytics.track('event_ui.filter.clicked', {
                            title: 'highlights'
                        });
                    }}
                />
            )}
            {categories.map((category) => {
                return (
                    <S_Category
                        key={category.worldMapSetId}
                        label={categoryTranslations.translateTitle(category.title)}
                        imgSrc={category.imageUrl}
                        isActive={category.worldMapSetId === categoryId}
                        onClick={() => {
                            setCategoryId(category.worldMapSetId);
                            analytics.track('event_ui.filter.clicked', {
                                title: category.title ?? 'unknown'
                            });
                        }}
                    />
                );
            })}
        </>
    );

    const showPc = ctxMapV2.isWide;
    const showMobile = (() => {
        if (ctxMapV2.isWide) {
            return false;
        }
        if (ctxMapV2.mobileModalState.size === 'small') {
            return true;
        }
        if (ctxMapV2.mobileModalState.size === 'large') {
            return !ctxModal.detailModal?.detail;
        }
    })();

    //get width: 100vw - 400px
    const width = window.innerWidth - 440;

    return (
        <S_MapV2Categories className={props.className}>
            {showPc && (
                <S_PcView
                    style={
                        ctxMapV2.isIframe
                            ? {
                                  top: 55,
                                  left: 10,
                                  width: width
                              }
                            : {
                                  top: 20,
                                  left: 20,
                                  width: width
                              }
                    }
                >
                    {content}
                </S_PcView>
            )}
            {showMobile && <S_MobileView $isIframe={ctxMapV2.isIframe}>{content}</S_MobileView>}
        </S_MapV2Categories>
    );
};

const S_MapV2Categories = styled.div``;

const S_PcView = styled.div`
    max-width: 100%;
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    position: absolute;

    z-index: 6;
`;

const S_MobileView = styled(ScrollHorizontal)<{ $isIframe: boolean }>`
    display: flex;
    flex-direction: row;
    width: 100vw;
    padding: 0 10px;
    position: absolute;
    top: ${(props) => (props.$isIframe ? 45 : 10)}px;
    left: 0px;
    z-index: 6;
    &::-webkit-scrollbar {
        display: none;
    }
`;

const S_Category = styled(MapV2CategoryButton)`
    margin-right: 10px;
    margin-bottom: 10px;
`;
